import _ from 'lodash';

import type { FilterCtrl, FilterType, Filters } from 'bundles/enterprise-admin-drawers/components/types';
import type {
  CallToActionPreset,
  FilterContextConsumer,
  FilterOptionTypeName,
  PersonalizationTag,
  Preset,
  PresetId,
  UsersListOnlyPreset,
} from 'bundles/enterprise-admin-messages/types';
import {
  CallToActionId,
  FilterOptionType,
  PersonalizationId,
  SortByDirection,
} from 'bundles/enterprise-admin-messages/types';
import getFilterContexts from 'bundles/enterprise-admin-messages/utils/getFilterContexts';
import { getUserFilterPreset } from 'bundles/enterprise-admin-user-filters/constants';
import {
  DEFAULT_DELETED_MEMBERS_COLUMNS,
  DEFAULT_INVITATIONS_COLUMNS,
  DEFAULT_INVITATIONS_PROGRAM_SCOPE_COLUMNS,
  DEFAULT_MEMBERS_COLUMNS,
  DEFAULT_MEMBERS_PROGRAM_SCOPE_COLUMNS,
} from 'bundles/enterprise-admin-users/constants';
import { AttributeSelections } from 'bundles/enterprise-admin-users/types';

import _t from 'i18n!nls/enterprise-admin-messages';

export const MAX_VISIBLE_FILTERS = 5;
export const MAX_FILTER_TEXT_LENGTH = 64;

export const SUBJECT_CHARACTER_LIMIT = 60;
export const CONTENT_CHARACTER_LIMIT = 999;

export const ADMIN_MESSAGES_TOS_AGREEMENT = 'ADMIN_MESSAGES_TOS_AGREEMENT';

export const getMessagesPresets = (isSingleProgramScope = false): Preset[] => {
  const greeting = _t('Hi #{learner},').replace('#{learner}', '<ptag>LEARNER_NAME</ptag>');
  const makeCmlEmail = (...paragraphs: string[]) =>
    `<co-content><text>${[greeting, ...paragraphs].join('</text><text>')}</text></co-content>`;
  return [
    // Make sure 'custom' is the first preset
    // since UI relies on its location to render it separately
    {
      id: 'custom',
      get title() {
        return _t('Write and send a custom message');
      },
      get caption() {
        return isSingleProgramScope
          ? _t('Include all learners who joined this program')
          : _t('Includes all learners who joined a learning program');
      },
      get description() {
        return isSingleProgramScope
          ? _t('All learners who joined this learning program')
          : _t('All learners who joined a learning program');
      },
      userListLabel: isSingleProgramScope ? _t('Joined this program') : _t('Joined a program'),
      emailTemplate: {
        subject: '',
        initialCML: {
          typeName: 'cml',
          definition: {
            dtdId: 'email/1',
            value: makeCmlEmail(_t("[Enter your message's content here]")),
          },
        },
      },
      availableFilters: isSingleProgramScope ? ['enrollments', 'contracts'] : ['enrollments', 'programs', 'contracts'],
      userFilter: getUserFilterPreset('joined'),
      defaultColumns: isSingleProgramScope ? DEFAULT_MEMBERS_PROGRAM_SCOPE_COLUMNS : DEFAULT_MEMBERS_COLUMNS,
      defaultSortBy: [
        {
          column: AttributeSelections.joinedAt,
          direction: SortByDirection.desc,
        },
      ],
      provideFilterContexts: ['learner', 'allow-empty'],
    },

    {
      id: 'remind-join',
      get title() {
        return isSingleProgramScope
          ? _t('Remind invited people to join this learning program')
          : _t('Remind invited people to join a learning program');
      },
      get caption() {
        return isSingleProgramScope
          ? _t('Includes all people who were invited but did not join this learning program')
          : _t('Includes all people who were invited but did not join a learning program');
      },
      get description() {
        return isSingleProgramScope
          ? _t('All people who were invited but did not join this learning program')
          : _t('All people who were invited but did not join a learning program');
      },
      get userListLabel() {
        return isSingleProgramScope
          ? _t('Invited but never joined this program')
          : _t('Invited but never joined a program');
      },
      emailTemplate: {
        get subject() {
          return _t('Your invitation is here');
        },
        initialCML: {
          typeName: 'cml',
          definition: {
            dtdId: 'email/1',
            value: makeCmlEmail(
              _t(
                'It’s easy to start learning with Coursera, but spots are limited on a first come, first served basis. You don’t need to decide which course to enroll in yet. Just take an important step today by claiming your place in our learning program.'
              )
            ),
          },
        },
      },
      availableFilters: isSingleProgramScope ? ['contracts'] : ['programs', 'contracts'],
      userFilter: getUserFilterPreset('invited'),
      defaultColumns: isSingleProgramScope ? DEFAULT_INVITATIONS_PROGRAM_SCOPE_COLUMNS : DEFAULT_INVITATIONS_COLUMNS,
      defaultSortBy: [
        {
          column: AttributeSelections.invitedAt,
          direction: SortByDirection.desc,
        },
      ],
      provideFilterContexts: ['invitee', 'program'],
    },

    {
      id: 'remind-enroll',
      get title() {
        return _t('Remind learners to enroll in a course or Specialization');
      },
      get caption() {
        return _t('Includes learners who joined but are not currently enrolled in anything');
      },
      get description() {
        return _t('Learners who joined but are not currently enrolled in anything');
      },
      get userListLabel() {
        return _t('Joined but have no current enrollments');
      },
      emailTemplate: {
        get subject() {
          return _t('Enroll and start learning');
        },
        initialCML: {
          typeName: 'cml',
          definition: {
            dtdId: 'email/1',
            value: makeCmlEmail(
              _t(
                'It’s great to see that you’ve joined #{program} on Coursera. Get started learning today by enrolling in a course or specialization!'
              ).replace('#{program}', '<ptag>PROGRAM_NAME</ptag>')
            ),
          },
        },
      },
      availableFilters: isSingleProgramScope ? ['contracts'] : ['programs', 'contracts'],

      userFilter: getUserFilterPreset('joined-not-enrolled'),

      defaultColumns: isSingleProgramScope ? DEFAULT_MEMBERS_PROGRAM_SCOPE_COLUMNS : DEFAULT_MEMBERS_COLUMNS,
      defaultSortBy: [
        {
          column: AttributeSelections.joinedAt,
          direction: SortByDirection.desc,
        },
      ],
      provideFilterContexts: ['learner', 'program'],
    },

    {
      id: 'remind-complete',
      get title() {
        return _t('Encourage learners to continue making progress');
      },
      get caption() {
        return _t('Includes learners currently enrolled');
      },
      get description() {
        return _t('Learners who enrolled');
      },
      get userListLabel() {
        return _t('Enrolled');
      },
      emailTemplate: {
        get subject() {
          return _t('Keep it up!');
        },
        initialCML: {
          typeName: 'cml',
          definition: {
            dtdId: 'email/1',
            value: makeCmlEmail(
              _t(
                'It’s great to see that you have started making progress in #{enrollment}. Making learning into a habit makes you much more likely to succeed. Keep working hard and learn relevant skills.'
              ).replace('#{enrollment}', '<ptag>ENROLLMENT_NAME</ptag>')
            ),
          },
        },
      },
      availableFilters: isSingleProgramScope ? ['contracts', 'enrollments'] : ['programs', 'contracts', 'enrollments'],
      userFilter: getUserFilterPreset('enrolled'),
      defaultColumns: isSingleProgramScope ? DEFAULT_MEMBERS_PROGRAM_SCOPE_COLUMNS : DEFAULT_MEMBERS_COLUMNS,
      defaultSortBy: [
        {
          column: AttributeSelections.joinedAt,
          direction: SortByDirection.desc,
        },
      ],
      provideFilterContexts: ['learner', 'program', 'enrollment'],
    },

    {
      id: 'remind-be-active-past-30-days',
      get title() {
        return _t('Remind inactive learners to use the learning program');
      },
      get caption() {
        return _t('Includes learners currently enrolled in a course but inactive for the past 30 days');
      },
      get description() {
        return _t('Learners currently enrolled in a course but inactive for the past 30 days');
      },
      get userListLabel() {
        return _t('Enrolled but inactive for the past 30 days');
      },
      emailTemplate: {
        get subject() {
          return _t('Get back on track');
        },
        initialCML: {
          typeName: 'cml',
          definition: {
            dtdId: 'email/1',
            value: makeCmlEmail(
              _t(
                'We noticed you haven’t been active in the program recently, please consider continuing #{enrollment} or try a new course!'
              ).replace('#{enrollment}', '<ptag>ENROLLMENT_NAME</ptag>'),
              _t('There’s a lot more to learn on Coursera. Get started today!')
            ),
          },
        },
      },
      availableFilters: isSingleProgramScope ? ['contracts'] : ['programs', 'contracts'],
      userFilter: getUserFilterPreset('enrolled-inactive'),

      defaultColumns: isSingleProgramScope ? DEFAULT_MEMBERS_PROGRAM_SCOPE_COLUMNS : DEFAULT_MEMBERS_COLUMNS,
      defaultSortBy: [
        {
          column: AttributeSelections.joinedAt,
          direction: SortByDirection.desc,
        },
      ],
      provideFilterContexts: ['learner', 'program'],
    },

    {
      id: 'remind-be-active-past-90-days',
      get title() {
        return _t('Notify long-term inactive learners');
      },
      get caption() {
        return _t('Includes learners who have been inactive for the past 90 days');
      },
      get description() {
        return _t('Learners who have been inactive for the past 90 days');
      },
      get userListLabel() {
        return _t('Inactive for the past 90 days');
      },
      emailTemplate: {
        get subject() {
          return _t('Start learning new skills');
        },
        initialCML: {
          typeName: 'cml',
          definition: {
            dtdId: 'email/1',
            value: makeCmlEmail(
              _t(
                'Didn’t get what you wanted out of #{program}? There are a lot of courses to explore. You can work towards a certification or learn just for fun at your own pace.'
              ).replace('#{program}', '<ptag>PROGRAM_NAME</ptag>'),
              _t('Take some time to look through Coursera’s catalog to see which course is right for you.')
            ),
          },
        },
      },
      availableFilters: isSingleProgramScope ? ['contracts'] : ['programs', 'contracts'],
      userFilter: getUserFilterPreset('inactive-90'),
      defaultColumns: isSingleProgramScope ? DEFAULT_MEMBERS_PROGRAM_SCOPE_COLUMNS : DEFAULT_MEMBERS_COLUMNS,
      defaultSortBy: [
        {
          column: AttributeSelections.joinedAt,
          direction: SortByDirection.desc,
        },
      ],
      provideFilterContexts: ['learner', 'program'],
    },

    {
      id: 'congratulate-achievement',
      get title() {
        return _t('Congratulate high performers on their achievement');
      },
      get caption() {
        return _t('Includes learners who have a completion in the past 30 days');
      },
      get description() {
        return _t('Learners who have a completion in the past 30 days');
      },
      get userListLabel() {
        return _t('At least 1 completion in the last 30 days');
      },
      emailTemplate: {
        get subject() {
          return _t('Well done!');
        },
        initialCML: {
          typeName: 'cml',
          definition: {
            dtdId: 'email/1',
            value: makeCmlEmail(
              _t('Congratulations on completing #{enrollment}! Your hard work has paid off.').replace(
                '#{enrollment}',
                '<ptag>ENROLLMENT_NAME</ptag>'
              ),
              _t(
                'There’s a lot more to learn on Coursera. Begin learning today by enrolling in another course or Specialization.'
              )
            ),
          },
        },
      },
      availableFilters: isSingleProgramScope ? ['enrollments', 'contracts'] : ['enrollments', 'programs', 'contracts'],

      userFilter: getUserFilterPreset('1-completion-in-30-days'),
      defaultColumns: isSingleProgramScope ? DEFAULT_MEMBERS_PROGRAM_SCOPE_COLUMNS : DEFAULT_MEMBERS_COLUMNS,
      defaultSortBy: [
        {
          column: AttributeSelections.joinedAt,
          direction: SortByDirection.desc,
        },
      ],
      provideFilterContexts: ['learner', 'enrollment'],
    },
  ];
};

export const getUsersListPresets = (isSingleProgramScope = false): UsersListOnlyPreset[] => [
  ...getMessagesPresets(isSingleProgramScope),
  {
    id: 'deleted',
    get userListLabel() {
      return isSingleProgramScope ? _t('Removed  from this program') : _t('Removed  from all programs');
    },
    availableFilters: isSingleProgramScope ? ['enrollments', 'contracts'] : ['enrollments', 'contracts', 'programs'],
    userFilter: getUserFilterPreset('removed'),

    defaultColumns: DEFAULT_DELETED_MEMBERS_COLUMNS,
    defaultSortBy: [
      {
        column: AttributeSelections.deletedAt,
        direction: SortByDirection.desc,
      },
    ],
  },
];

export const getCallToActionPresets = (): CallToActionPreset[] => [
  {
    id: CallToActionId.JOIN_NOW,
    get title() {
      return _t('Join program');
    },
    get caption() {
      return _t(
        'Links to the page where invited learners can join a Learning Program. If the learner was invited to multiple Learning Programs they will receive a message for each Learning Program they were invited to unless a Learning Program filter was used when defining the audience for this message.'
      );
    },
    consumeFilterContexts: ['invitee', 'program'],
  },
  {
    id: CallToActionId.CONTINUE_LEARNING,
    get title() {
      return _t('Continue learning');
    },
    get caption() {
      return _t(
        'Links to the Course the learner is enrolled in or the next course in a Specialization or Professional Certificate that the learner is enrolled in. If the learner has multiple enrollments they will receive a message for each enrollment unless an enrollment filter was used when defining the audience for this message.'
      );
    },
    consumeFilterContexts: ['learner', 'enrollment'],
  },
  {
    id: CallToActionId.FIND_A_COURSE,
    get title() {
      return _t('Find a course');
    },
    get caption() {
      return _t(
        'Links to the Learning Program homepage where learners can view available courses. If the learner joined multiple Learning Programs they will receive a message for each Learning Program they joined unless a Learning Program filter was used when defining the audience for this message.'
      );
    },
    consumeFilterContexts: ['learner'],
  },
  {
    id: CallToActionId.DO_NOT_INCLUDE,
    get title() {
      return _t('Do not include a call to action (not recommended)');
    },
    get caption() {
      return _t(
        'Use this option only if you are sending a broad announcement and don’t expect learners to visit the learning program after reading the message.'
      );
    },
    consumeFilterContexts: ['allow-empty'],
  },
];

export const getFilter = (filterType: FilterType): FilterCtrl => {
  const AVAILABLE_FILTERS: FilterCtrl[] = [
    {
      type: 'enrollments',
      isCustomizable: true,
      get title() {
        return _t('Current enrollments');
      },
      get label() {
        return _t('Courses');
      },
      get emptyText() {
        return _t('Any course or Specialization');
      },
      skipEmpty: true,
      minSearchLength: 2,
      provideFilterContexts: ['enrollment'],
      preSorted: true,
    },
    {
      type: 'programs',
      isCustomizable: true,
      get title() {
        return _t('Learning programs');
      },
      get label() {
        return _t('Programs');
      },
      get emptyText() {
        return _t('Any learning program');
      },
      skipEmpty: false,
      minSearchLength: 1,
      provideFilterContexts: ['program'],
    },
    {
      type: 'contracts',
      isCustomizable: true,
      get title() {
        return _t('Contracts');
      },
      get label() {
        return _t('Contracts');
      },
      get emptyText() {
        return _t('Any contract');
      },
      skipEmpty: false,
      minSearchLength: 1,
      provideFilterContexts: [],
    },
  ];

  const AVAILABLE_FILTERS_MAP = _.keyBy(AVAILABLE_FILTERS, 'type');

  return AVAILABLE_FILTERS_MAP[filterType];
};

export const getFilterOptionTypeName = (filterOptionType: FilterOptionType): FilterOptionTypeName => {
  switch (filterOptionType) {
    case FilterOptionType.COURSE:
      return {
        get full() {
          return _t('Course');
        },
        get plural() {
          return _t('Courses');
        },
      };
    case FilterOptionType.PROGRAM:
      return {
        get full() {
          return _t('Learning Program');
        },
        get plural() {
          return _t('Programs');
        },
      };
    case FilterOptionType.SPECIALIZATION:
      return {
        get full() {
          return _t('Specialization');
        },
        get plural() {
          return _t('Specializations');
        },
      };
    case FilterOptionType.CERTIFICATE:
      return {
        get full() {
          return _t('Professional Certificate');
        },
        get plural() {
          return _t('Certificates');
        },
      };
    case FilterOptionType.CONTRACT:
      return {
        get full() {
          return _t('Contract');
        },
        get plural() {
          return _t('Contracts');
        },
      };
    case FilterOptionType.BUSINESS_UNIT:
      return {
        get full() {
          return _t('Business Unit');
        },
        get plural() {
          return _t('Businesses');
        },
      };
    case FilterOptionType.LOCATION:
      return {
        get full() {
          return _t('Location');
        },
        get plural() {
          return _t('Locations');
        },
      };
    case FilterOptionType.MANAGER:
      return {
        get full() {
          return _t('Manager');
        },
        get plural() {
          return _t('Managers');
        },
      };
    case FilterOptionType.JOB_TITLE:
      return {
        get full() {
          return _t('Job Title');
        },
        get plural() {
          return _t('Job Titles');
        },
      };
    default:
      return { full: '', plural: '' };
  }
};

export const getPersonalizationPresets = (): PersonalizationTag[] => [
  {
    value: PersonalizationId.LEARNER_NAME,
    get caption() {
      return _t("The NAME variable will be replaced by the person's first name.");
    },
    consumeFilterContexts: [],
  },
  {
    value: PersonalizationId.PROGRAM_NAME,
    get caption() {
      return _t(
        'This tag will be replaced by the name of the program associated with the learner. If the learner is associated with multiple programs they will receive one message for each program unless a program filter was used to define the audience of this message.'
      );
    },
    consumeFilterContexts: ['program'],
  },
  {
    value: PersonalizationId.ENROLLMENT_NAME,
    get caption() {
      return _t(
        'This tag will be replaced by the name of the Course, Specialization, or Professional Certificate that the learner is enrolled in. If the learner has multiple enrollments they will receive one message for each enrollment unless an enrollment filter was used to define the audience of this message.'
      );
    },
    consumeFilterContexts: ['enrollment'],
  },
];

export const getMessagesPresetById = (id: PresetId, isSingleProgramScope = false): Preset => {
  const presets = getMessagesPresets(isSingleProgramScope);
  let preset = _.find(presets, (p) => p.id === id);

  if (id === 'remind-assigned-learners') {
    preset = _.find(presets, (p) => p.id === 'custom');
  }

  if (!preset) {
    throw new Error(`Invalid preset id: ${id}, expected one of: ${presets.map((p) => p.id).join(',')}`);
  }
  return preset;
};

export const getUsersListPresetById = (id: PresetId, isSingleProgramScope = false): UsersListOnlyPreset => {
  const presets = getUsersListPresets(isSingleProgramScope);
  let preset = _.find(presets, (p) => p.id === id);
  if (id === 'remind-assigned-learners') {
    preset = _.find(presets, (p) => p.id === 'custom');
  }
  if (!preset) {
    throw new Error(`Invalid preset id: ${id}, expected one of: ${presets.map((p) => p.id).join(',')}`);
  }
  return preset;
};

export const getCallToActionPresetById = (actionId: CallToActionId): CallToActionPreset => {
  const callToActionPresets = getCallToActionPresets();
  const callToActionPreset = _.find(callToActionPresets, (p) => p.id === actionId);
  if (!callToActionPreset) {
    throw new Error(
      `Invalid call to action preset id: ${actionId}, expected one of ${callToActionPresets.map((p) => p.id).join(',')}`
    );
  }
  return callToActionPreset;
};

export const getPersonalizationOptionById = (tagId: PersonalizationId): PersonalizationTag => {
  const personalizationPresets = getPersonalizationPresets();
  const personalizationPreset = _.find(personalizationPresets, (p) => p.value === tagId);
  if (!personalizationPreset) {
    throw new Error(
      `Invalid personalization preset id: ${tagId}, expected one of ${personalizationPresets
        .map((p) => p.value)
        .join(',')}`
    );
  }
  return personalizationPreset;
};

/**
 * A higher-order function that creates a filter that could find valid consumers based on given presetId and filters.
 * @param consumers List of consumers.
 * @return A function in the shape of (presetId, filters) => consumer[], returns valid consumers
 */
const getValidContextConsumerFunc =
  <T extends FilterContextConsumer>(consumers: Array<T>) =>
  (presetId: PresetId, filters: Filters): T[] => {
    const preset = getMessagesPresetById(presetId);
    const filterTypesUsed = Object.keys(filters).filter((k) => filters[k].length > 0) as FilterType[];
    const filtersUsed = filterTypesUsed.map(getFilter);

    const contexts = getFilterContexts([preset, ...filtersUsed]);

    return consumers.filter((consumer) => _.every(consumer.consumeFilterContexts, (fc) => _.includes(contexts, fc)));
  };

export const getValidCallToActionPresets = getValidContextConsumerFunc(getCallToActionPresets());
export const getValidPersonalizationPresets = getValidContextConsumerFunc(getPersonalizationPresets());
